import React, { useCallback, useEffect, useRef, useState } from 'react';
import RowV2 from '@/components/v2/RowV2';
import getDateTimeAgo from '@/lib/getDateTimeAgo';
import IconV2 from '@/components/v2/IconV2';
import Spinner from '@/components/Spinner';
import Button from '../../Button.jsx';
import Input from '@/components/Input.jsx';
import Tag from '@/components/Tag.jsx';

const SnapshotListRow = ({ snapshot, workflowIsDraft, handleClose, customActions = {} }) => {
  const [isSwitching, setIsSwitching] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [description, setDescription] = useState(snapshot?.description || '');
  const inputRef = useRef();

  const isDraftSnapshot = snapshot?.version === 'draft';

  const handleSnapshotSwitch = useCallback(async () => {
    if (customActions.onOpenInEditor) {
      setIsSwitching(true);
      try {
        await customActions.onOpenInEditor(snapshot, workflowIsDraft);
        handleClose();
      } finally {
        setIsSwitching(false);
      }
    } else {
      console.warn('onOpenInEditor action not configured');
    }
  }, [snapshot, workflowIsDraft, handleClose, customActions]);

  const handleSnapshotPublish = useCallback(async () => {
    if (customActions.onPublish) {
      setIsPublishing(true);
      try {
        await customActions.onPublish(snapshot);
      } finally {
        setIsPublishing(false);
      }
    } else {
      console.warn('onPublish action not configured');
    }
  }, [snapshot, customActions]);

  const handleSnapshotUpdate = useCallback(
    async (updatedData) => {
      if (customActions.onUpdate) {
        setIsSaving(true);
        try {
          await customActions.onUpdate(snapshot, updatedData);
          setIsEditing(false);
        } finally {
          setIsSaving(false);
        }
      }
    },
    [snapshot, customActions]
  );

  const handleSeeChanges = useCallback(() => {
    if (customActions.onSeeChanges) {
      customActions.onSeeChanges(snapshot);
    } else {
      console.warn('onSeeChanges action not configured');
    }
  }, [snapshot, customActions]);

  const handleDescriptionKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        setIsEditing(false);
        setDescription(snapshot?.description || '');
      }
      if (e.key === 'Enter' && !e.shiftKey && !isSaving) {
        e.preventDefault();
        e.currentTarget.blur();
        handleSnapshotUpdate({ description: e.target.value });
      }
    },
    [snapshot, isSaving, handleSnapshotUpdate]
  );

  useEffect(() => {
    if (isEditing) {
      const input = inputRef.current;
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }
  }, [isEditing]);

  return (
    <div className={`snapshot-list-row ${isDraftSnapshot ? 'draft' : ''}`.trim()}>
      <RowV2 className="borderless" style={{ height: 30 }}>
        <RowV2.Left>
          <span className="snapshot-version">{snapshot.version}</span>
          <div className="snapshot-tags">
            {snapshot?.isPublished && (
              <Tag variant="blue" size="sm">
                <span>Published</span>
              </Tag>
            )}
            {snapshot?.snapshotStatus === 'unhealthy' && (
              <Tag variant="warn" size="sm">
                <span>Unhealthy</span>
              </Tag>
            )}
          </div>
        </RowV2.Left>
      </RowV2>
      <span className="snapshot-created-on" title={`Created ${getDateTimeAgo(snapshot?.createdOn)} by ${snapshot?.createdBy?.name}`}>
        <span className="time">{getDateTimeAgo(snapshot?.createdOn)} </span>
        <span>by </span>
        <span className="name">{snapshot?.createdBy?.name}</span>
      </span>
      {!isDraftSnapshot && customActions.onUpdate && (
        <div className={'snapshot-description'}>
          {isSaving && (
            <div className="snapshot-textarea-loading">
              <IconV2 icon={Spinner} />
            </div>
          )}
          {!isEditing && (
            <p className={`${description ? '' : 'empty'}`} onClick={() => setIsEditing(true)}>
              {description || 'Add description...'}
            </p>
          )}
          {isEditing && <Input ref={inputRef} className={'long ghost'} type="textarea" value={description} onChange={(e) => setDescription(e.target.value)} onKeyDown={handleDescriptionKeyDown} onBlur={() => setIsEditing(false)} autoSize rows={1} />}
        </div>
      )}
      <RowV2 className="borderless" style={{ height: 24 }}>
        <RowV2.Left>
          {customActions.onOpenInEditor && (
            <Button size={'sm'} onClick={handleSnapshotSwitch} className={'link slim'} disabled={isSwitching} loading={isSwitching} title="Open this version in the editor">
              {isDraftSnapshot ? 'Restore Draft in Editor' : 'Open in Editor'}
            </Button>
          )}
          {!snapshot?.isPublished && !isDraftSnapshot && customActions.onPublish && (
            <>
              {customActions.onOpenInEditor && <span className="dot">•</span>}
              <Button size={'sm'} onClick={handleSnapshotPublish} className={'link slim'} disabled={isPublishing} loading={isPublishing} title="Publish this version">
                Publish
              </Button>
            </>
          )}
          {!isDraftSnapshot && customActions.onSeeChanges && (
            <>
              {(customActions.onOpenInEditor || customActions.onPublish) && <span className="dot">•</span>}
              <Button size={'sm'} onClick={handleSeeChanges} className={'link slim'} title="Compare this version with the current draft">
                See Changes
              </Button>
            </>
          )}
        </RowV2.Left>
      </RowV2>
    </div>
  );
};

export default SnapshotListRow;
